import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  LayoutDashboard,
  Users,
  UserCheck,
  MapPin,
  Briefcase,
  ClipboardList,
  FileText,
  FileSpreadsheet,
  Wrench,
  CheckSquare,
  Settings,
  LogOut,
  Menu,
  ChevronDown,
  UsersRound,
  FileBox,
  Globe,
  Receipt,
  FolderOpen,
  Search,
  Calendar,
  Truck,
  ClipboardCheck,
  Building2,
  BarChart2,
  Shield,
  Radio,
  Inbox,
  Target,
  FolderKanban,
  ShieldCheck,
} from 'lucide-react';
import axios from 'axios';
import NotificationBell from '@/components/NotificationBell';
import { PLATFORM } from '@/config/platform';

const API = process.env.REACT_APP_BACKEND_URL;

// PHS Logo Component 
const PHSLogo = ({ collapsed = false }) => (
  <div className="flex items-center gap-2">
    {collapsed ? (
      <div className="w-12 h-12 rounded-xl overflow-hidden shadow-lg bg-[#0a1a1a] p-1">
        <img src="/phs-logo.png" alt="PHS" className="w-full h-full object-contain" />
      </div>
    ) : (
      <div className="flex items-center gap-3">
        <div className="w-16 h-16 rounded-xl overflow-hidden shadow-lg bg-[#0a1a1a] p-1">
          <img src="/phs-logo.png" alt="PHS" className="w-full h-full object-contain" />
        </div>
        <div>
          <div className="font-bold text-[#0F766E] text-sm leading-tight">{PLATFORM.name}</div>
          <div className="text-xs text-gray-500">Platform</div>
        </div>
      </div>
    )}
  </div>
);

const navItems = [
  { label: 'Dashboard', icon: LayoutDashboard, path: '/' },
  { type: 'divider', label: 'Operations' },
  { label: 'Dispatch Board', icon: Truck, path: '/dispatch', highlight: true },
  { label: 'Enquiry Triage', icon: Inbox, path: '/triage', highlight: true },
  { label: 'Pipeline Board', icon: BarChart2, path: '/pipeline', highlight: true },
  { label: 'Jobs', icon: Briefcase, path: '/jobs', highlight: true },
  { label: 'FM System', icon: Truck, path: '/fm', highlight: true },
  { label: 'Projects', icon: FolderKanban, path: '/projects', highlight: true },
  { label: 'Operations & Compliance', icon: Shield, path: '/operations', highlight: true },
  { label: 'Command Centre', icon: Radio, path: '/command-centre', highlight: true },
  { label: 'Board Pack', icon: BarChart2, path: '/board-pack', highlight: true },
  { label: 'Control Dashboard', icon: Target, path: '/lhs/reports', highlight: true },
  { type: 'divider', label: 'PPM System' },
  { label: 'PPM Library', icon: ClipboardCheck, path: '/ppm-library', highlight: true },
  { label: 'PPM Schedule', icon: Calendar, path: '/ppm-schedule', highlight: true },
  { label: 'PPM Jobs', icon: Wrench, path: '/ppm-jobs', highlight: true },
  { label: 'Assets', icon: Building2, path: '/assets', highlight: true },
  { type: 'divider', label: 'Commercial' },
  { label: 'PHS Members', icon: ShieldCheck, path: '/phs-members', highlight: true },
  { label: 'Finance', icon: Receipt, path: '/finance', highlight: true },
  { label: 'Quoted Works', icon: FileText, path: '/quoted-works', highlight: true },
  { label: 'Variations', icon: FileSpreadsheet, path: '/variations', highlight: true },
  { label: 'Invoice Control', icon: Receipt, path: '/invoice-control', highlight: true },
  { label: 'Contracts', icon: FileSpreadsheet, path: '/contracts' },
  { label: 'Client Reports', icon: BarChart2, path: '/client-reports' },
  { label: 'Calendar', icon: Calendar, path: '/calendar' },
  { label: 'Leads', icon: Users, path: '/leads' },
  { label: 'Clients', icon: UserCheck, path: '/clients' },
  { label: 'Parks/Sites', icon: MapPin, path: '/parks' },
  { type: 'divider', label: 'Documents' },
  { label: 'Documents', icon: FolderOpen, path: '/documents' },
  { label: 'Quotes', icon: FileSpreadsheet, path: '/quotes' },
  { label: 'Invoices', icon: Receipt, path: '/invoices' },
  { type: 'divider', label: 'Resources' },
  { label: 'Contractors', icon: Wrench, path: '/contractors' },
  { label: 'Tasks', icon: CheckSquare, path: '/tasks' },
  { type: 'divider', label: 'Admin', roles: ['admin', 'manager'] },
  { label: 'Staff', icon: UsersRound, path: '/staff', roles: ['admin', 'manager'] },
  { label: 'Survey Templates', icon: FileBox, path: '/survey-templates', roles: ['admin'] },
  { label: 'Portal Submissions', icon: Globe, path: '/portal-management' },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState({ jobs: [], clients: [], leads: [], contractors: [], ppm_assets: [], fm_jobs: [] });
  const [isSearching, setIsSearching] = useState(false);

  // Global search with keyboard shortcut
  useEffect(() => {
    const down = (e) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setSearchOpen((open) => !open);
      }
    };
    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, []);

  // Search API call
  useEffect(() => {
    if (!searchQuery || searchQuery.length < 2) {
      setSearchResults({ jobs: [], clients: [], leads: [], contractors: [], ppm_assets: [], fm_jobs: [] });
      return;
    }

    const timer = setTimeout(async () => {
      setIsSearching(true);
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API}/api/search`, {
          params: { q: searchQuery },
          headers: { Authorization: `Bearer ${token}` }
        });
        setSearchResults(response.data);
      } catch (error) {
        console.error('Search error:', error);
      } finally {
        setIsSearching(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleSearchSelect = (type, id) => {
    setSearchOpen(false);
    setSearchQuery('');
    if (type === 'job') navigate('/jobs');
    else if (type === 'client') navigate('/clients');
    else if (type === 'lead') navigate('/leads');
    else if (type === 'contractor') navigate(`/contractors?id=${id}`);
    else if (type === 'ppm_asset') navigate('/ppm');
    else if (type === 'fm_job') navigate(`/fm?job=${id}`);
  };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U';
  };

  const canViewItem = (item) => {
    if (!item.roles) return true;
    return item.roles.includes(user?.role);
  };

  const NavLink = ({ item }) => {
    const isActive = location.pathname === item.path;
    const Icon = item.icon;
    
    return (
      <Link
        to={item.path}
        data-testid={`nav-${item.label.toLowerCase().replace(/[\s\/]/g, '-')}`}
        className={`sidebar-link ${isActive ? 'active' : 'text-gray-600 hover:text-gray-900'} ${item.highlight && !isActive ? 'border border-teal-200 bg-teal-50' : ''}`}
        onClick={() => setMobileOpen(false)}
      >
        <Icon className="h-4 w-4" />
        {sidebarOpen && <span>{item.label}</span>}
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-teal-50/30 flex">
      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 bg-white border-r border-gray-200 transition-all duration-300 ${
          sidebarOpen ? 'w-64' : 'w-16'
        } ${mobileOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-20 px-4 border-b border-gray-200">
            <PHSLogo collapsed={!sidebarOpen} />
            <Button
              variant="ghost"
              size="icon"
              className="hidden lg:flex"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              data-testid="toggle-sidebar-btn"
            >
              <Menu className="h-4 w-4" />
            </Button>
          </div>

          {/* Navigation */}
          <ScrollArea className="flex-1 py-4">
            <nav className="px-2 space-y-1">
              {navItems.map((item, index) => {
                if (!canViewItem(item)) return null;
                
                if (item.type === 'divider') {
                  return sidebarOpen && (
                    <div key={index} className="pt-4 pb-2 px-3">
                      <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">
                        {item.label}
                      </span>
                    </div>
                  );
                }
                
                return <NavLink key={item.path} item={item} />;
              })}
            </nav>
          </ScrollArea>

          {/* Settings at bottom */}
          <div className="p-2 border-t border-gray-200">
            <Link
              to="/settings"
              data-testid="nav-settings"
              className={`sidebar-link ${location.pathname === '/settings' ? 'active' : 'text-gray-600 hover:text-gray-900'}`}
            >
              <Settings className="h-4 w-4" />
              {sidebarOpen && <span>Settings</span>}
            </Link>
          </div>
        </div>
      </aside>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Main content */}
      <div className={`flex-1 ${sidebarOpen ? 'lg:ml-64' : 'lg:ml-16'} transition-all duration-300 overflow-x-hidden`}>
        {/* Header */}
        <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-md border-b border-gray-200 h-16">
          <div className="flex items-center justify-between h-full px-4 lg:px-6">
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden shrink-0"
              onClick={() => setMobileOpen(true)}
              data-testid="mobile-menu-btn"
            >
              <Menu className="h-5 w-5" />
            </Button>

            {/* Global Search Button */}
            <Button
              variant="outline"
              className="hidden md:flex items-center gap-2 text-stone-500 hover:text-stone-900 w-64"
              onClick={() => setSearchOpen(true)}
              data-testid="global-search-btn"
            >
              <Search className="h-4 w-4" />
              <span className="flex-1 text-left">Search...</span>
              <kbd className="pointer-events-none hidden md:inline-flex h-5 select-none items-center gap-1 rounded border bg-stone-100 px-1.5 font-mono text-[10px] font-medium text-stone-600">
                <span className="text-xs">⌘</span>K
              </kbd>
            </Button>

            <div className="flex-1 min-w-0" />

            {/* Quick Actions for Surveyors */}
            {user?.role === 'surveyor' && (
              <Button 
                variant="outline" 
                size="sm" 
                className="mr-4 border-teal-300 text-teal-700 hover:bg-teal-50"
                onClick={() => navigate('/jobs')}
              >
                <ClipboardList className="h-4 w-4 mr-2" />
                My Jobs
              </Button>
            )}

            {/* Notification Bell */}
            <NotificationBell />

            {/* User menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu-btn">
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className="bg-gradient-to-br from-[#1F7A78] to-[#0dbdad] text-white text-xs">
                      {getInitials(user?.name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="hidden md:block text-left">
                    <p className="text-sm font-medium text-gray-900">{user?.name}</p>
                    <p className="text-xs text-gray-500 capitalize">{user?.role}</p>
                  </div>
                  <ChevronDown className="h-4 w-4 text-gray-400" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium">{user?.name}</p>
                  <p className="text-xs text-gray-500">{user?.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/settings" className="cursor-pointer">
                    <Settings className="mr-2 h-4 w-4" />
                    Settings
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} className="text-red-600 cursor-pointer" data-testid="logout-btn">
                  <LogOut className="mr-2 h-4 w-4" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 lg:p-6">
          {children}
        </main>
      </div>

      {/* Global Search Dialog */}
      <CommandDialog open={searchOpen} onOpenChange={setSearchOpen} shouldFilter={false}>
        <CommandInput 
          placeholder="Search jobs, clients, contractors, FM jobs..." 
          value={searchQuery}
          onValueChange={setSearchQuery}
        />
        <CommandList>
          {isSearching ? (
            <div className="p-4 text-center text-sm text-stone-500">
              Searching...
            </div>
          ) : searchQuery.length < 2 ? (
            <CommandEmpty>Type at least 2 characters to search...</CommandEmpty>
          ) : (
            <>
              {searchResults.jobs?.length === 0 && 
               searchResults.clients?.length === 0 && 
               searchResults.leads?.length === 0 &&
               searchResults.contractors?.length === 0 &&
               searchResults.ppm_assets?.length === 0 &&
               searchResults.fm_jobs?.length === 0 && (
                <CommandEmpty>No results found.</CommandEmpty>
              )}
              
              {searchResults.jobs?.length > 0 && (
                <CommandGroup heading="Jobs">
                  {searchResults.jobs.map((job) => (
                    <CommandItem 
                      key={job.id}
                      value={`${job.job_number} ${job.title} ${job.property_postcode}`}
                      onSelect={() => handleSearchSelect('job', job.id)}
                      className="cursor-pointer"
                    >
                      <Briefcase className="mr-2 h-4 w-4 text-stone-400" />
                      <div className="flex-1">
                        <span className="font-medium">{job.job_number}</span>
                        <span className="mx-2 text-stone-400">|</span>
                        <span>{job.title}</span>
                      </div>
                      <span className="text-xs text-stone-400">{job.property_postcode}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
              
              {searchResults.fm_jobs?.length > 0 && (
                <CommandGroup heading="FM Jobs">
                  {searchResults.fm_jobs.map((job) => (
                    <CommandItem 
                      key={job.id}
                      value={`${job.job_reference} ${job.site_name} ${job.postcode}`}
                      onSelect={() => handleSearchSelect('fm_job', job.id)}
                      className="cursor-pointer"
                    >
                      <Truck className="mr-2 h-4 w-4 text-teal-500" />
                      <div className="flex-1">
                        <span className="font-medium">{job.job_reference}</span>
                        <span className="mx-2 text-stone-400">|</span>
                        <span>{job.site_name}</span>
                      </div>
                      <span className="text-xs text-stone-400">{job.postcode}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
              
              {searchResults.clients?.length > 0 && (
                <CommandGroup heading="Clients">
                  {searchResults.clients.map((client) => (
                    <CommandItem 
                      key={client.id}
                      value={`client-${client.id}`}
                      onSelect={() => handleSearchSelect('client', client.id)}
                      className="cursor-pointer"
                    >
                      <UserCheck className="mr-2 h-4 w-4 text-stone-400" />
                      <span className="font-medium">{client.name}</span>
                      {client.company_name && (
                        <span className="ml-2 text-stone-400">{client.company_name}</span>
                      )}
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
              
              {searchResults.contractors?.length > 0 && (
                <CommandGroup heading="Contractors">
                  {searchResults.contractors.map((contractor) => (
                    <CommandItem 
                      key={contractor.id}
                      value={`contractor-${contractor.id}`}
                      onSelect={() => handleSearchSelect('contractor', contractor.id)}
                      className="cursor-pointer"
                    >
                      <Wrench className="mr-2 h-4 w-4 text-amber-500" />
                      <div className="flex-1">
                        <span className="font-medium">{contractor.company_name || contractor.name}</span>
                        {contractor.contact_name && (
                          <span className="ml-2 text-stone-400">({contractor.contact_name})</span>
                        )}
                      </div>
                      <span className="text-xs text-stone-400">{contractor.phone}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
              
              {searchResults.ppm_assets?.length > 0 && (
                <CommandGroup heading="PPM Assets">
                  {searchResults.ppm_assets.map((asset) => (
                    <CommandItem 
                      key={asset.agreement_id || asset.id}
                      value={`ppm_asset-${asset.agreement_id || asset.id}`}
                      onSelect={() => handleSearchSelect('ppm_asset', asset.agreement_id || asset.id)}
                      className="cursor-pointer"
                    >
                      <ClipboardCheck className="mr-2 h-4 w-4 text-blue-500" />
                      <div className="flex-1">
                        <span className="font-medium">{asset.name}</span>
                        <span className="mx-2 text-stone-400">|</span>
                        <span>{asset.site_name}</span>
                      </div>
                      <span className="text-xs text-stone-400">{asset.agreement_id}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
              
              {searchResults.leads?.length > 0 && (
                <CommandGroup heading="Leads">
                  {searchResults.leads.map((lead) => (
                    <CommandItem 
                      key={lead.id}
                      value={`lead-${lead.id}`}
                      onSelect={() => handleSearchSelect('lead', lead.id)}
                      className="cursor-pointer"
                    >
                      <Users className="mr-2 h-4 w-4 text-stone-400" />
                      <span className="font-medium">{lead.name}</span>
                      <span className="ml-2 text-xs text-stone-400">{lead.email}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
            </>
          )}
        </CommandList>
      </CommandDialog>
    </div>
  );
}
